/**
 * The whole i18n runtime: two flat catalogues and a lookup with a fallback
 * chain. No library, on purpose — the requirement is copy that a maintainer can
 * fix by editing one line of plain text, and a flat file plus this file
 * delivers exactly that.
 *
 * Adding a third language is one more JSON file and one more entry below.
 */
import de from './de.json';
import en from './en.json';

export const SUPPORTED_LANGUAGES = ['en', 'de'] as const;

export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Language = 'en';

export type TranslationParams = Record<string, string | number>;

/**
 * Every key English defines, as a type. A typo'd key is then a `tsc` failure
 * rather than a warning in a console nobody is reading and a raw dotted string
 * rendered where a sentence belongs.
 */
export type TranslationKey = keyof typeof en;

export type Translate = (key: TranslationKey, params?: TranslationParams) => string;

/**
 * German is typed against English's key set, so a key added to one catalogue
 * and forgotten in the other fails the typecheck. The parity test still earns
 * its place: types cannot see an *extra* German key, nor an empty value.
 */
export const catalogues: Record<Language, Record<TranslationKey, string>> = { en, de };

const isSupported = (value: string): value is Language =>
  (SUPPORTED_LANGUAGES as readonly string[]).includes(value);

/**
 * Region is deliberately ignored: `de-AT` and `de-CH` both read German. An
 * unrecognised value returns undefined so callers can fall through the
 * resolution chain rather than being forced to English.
 */
export function normalizeLanguage(value: string | null | undefined): Language | undefined {
  if (!value) return undefined;
  const base = value.trim().toLowerCase().split('-')[0];
  return isSupported(base) ? base : undefined;
}

/** An empty value is a translator's unfinished line, not an intentional blank. */
function lookup(language: Language, key: TranslationKey): string | undefined {
  const value = catalogues[language][key];
  return value === undefined || value.trim() === '' ? undefined : value;
}

// One line per distinct problem. A missing key in a countdown would otherwise
// log once a second for as long as the page is open.
const alreadyWarned = new Set<string>();

function warnOnce(message: string): void {
  if (alreadyWarned.has(message)) return;
  alreadyWarned.add(message);
  console.warn(message);
}

function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (placeholder, name: string) =>
    name in params ? String(params[name]) : placeholder,
  );
}

/**
 * A missing key falls back to English, then to the key itself. Rendering a raw
 * key is a visible bug in development and a survivable one in production; a
 * crash or a blank screen is neither.
 */
export function translate(
  language: Language,
  key: TranslationKey,
  params?: TranslationParams,
): string {
  const value = lookup(language, key);
  if (value !== undefined) return interpolate(value, params);

  const fallback = lookup(DEFAULT_LANGUAGE, key);
  if (fallback !== undefined) {
    warnOnce(`[i18n] no ${language} translation for "${key}" — falling back to ${DEFAULT_LANGUAGE}`);
    return interpolate(fallback, params);
  }

  warnOnce(`[i18n] "${key}" is in no catalogue`);
  return key;
}
